import { useState } from 'react';

import dayjs from 'dayjs';

import {
  Icons,
  Skeleton,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Box,
  Stack,
} from '@cast/design-system';

import { RangePicker, DateRangeFilter } from '@cast/shared-date-ui';
import { useEnterprisePlatformUsageDetailQuery } from 'hooks/queries/enterprise/useEnterprisePlatformUsageDetailQuery';
import { useOrganizations } from '@cast/shared-organization-context-data-access';

import { DailyUsageTable } from './_components/DailyUsageTable';
import { DetailOverviewCard } from './_components/DetailOverviewCard';
import { EnterpriseEntitiesTable } from './_components/EnterpriseEntitiesTable';
import { PlatformUsageFooter } from './_components/PlatformUsageFooter';
import {
  UsageOverTimeChart,
  type UsageMode,
} from './_components/UsageOverTimeChart';

import { FailedToLoad } from '@cast/shared-messages-ui';

// Limit matches the maximum number of unique colors that we can display
const MAX_SELECTED_ORGANIZATIONS = 13;

const CHART_RANGE_PRESETS = [
  DateRangeFilter.ThisMonth,
  DateRangeFilter.PreviousMonth,
  DateRangeFilter.Last7Days,
  DateRangeFilter.Last30Days,
  DateRangeFilter.Last3Months,
  DateRangeFilter.Last6Months,
  DateRangeFilter.Last9Months,
  DateRangeFilter.Last12Months,
] as const;

type EnterprisePlatformUsageDetailProps = {
  feature: string;
};

const NUMBER_OF_DAYS_IN_13_MONTHS = 397;

export const EnterprisePlatformUsageDetail = ({
  feature,
}: EnterprisePlatformUsageDetailProps) => {
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<'perOrganization' | 'daily'>(
    'perOrganization'
  );
  const [mode, setMode] = useState<UsageMode>('organizations');
  const { enterpriseOrgId } = useOrganizations();
  const enterpriseId = enterpriseOrgId ?? '';

  // Current month – always used for the overview card
  const currentMonthFrom = dayjs().startOf('month').format('YYYY-MM-DD');
  const currentMonthTo = dayjs().endOf('month').format('YYYY-MM-DD');

  const { data: overviewData, isLoading: isOverviewLoading } =
    useEnterprisePlatformUsageDetailQuery({
      enterpriseId,
      feature,
      periodFrom: currentMonthFrom,
      periodTo: currentMonthTo,
    });

  // User-selected range – used for chart and table
  const [periodFrom, setPeriodFrom] = useState(currentMonthFrom);
  const [periodTo, setPeriodTo] = useState(currentMonthTo);
  // Store color map from chart to sync with table
  const [entityColorMap, setEntityColorMap] = useState<Map<string, string>>(
    new Map()
  );
  const { data, isLoading, isError, refetch } =
    useEnterprisePlatformUsageDetailQuery({
      enterpriseId,
      feature,
      periodFrom,
      periodTo,
    });

  const handleRowsSelected = (ids: string[]) => {
    // Limit selection to max 13 organizations
    setSelectedRows(ids.slice(0, MAX_SELECTED_ORGANIZATIONS));
  };

  if (!isLoading && isError) {
    return <FailedToLoad refresh={refetch} />;
  }

  const overviewDetail = overviewData?.detail;
  const detail = data?.detail;
  const entities = detail?.entities ?? [];
  const unit = detail?.unit ?? '';

  const rangePicker = (
    <RangePicker
      sx={{ width: 250 }}
      filterPresets={CHART_RANGE_PRESETS}
      initialPreset={DateRangeFilter.ThisMonth}
      hideTimezonePicker
      allowTimeInput={false}
      maxNumberOfDays={NUMBER_OF_DAYS_IN_13_MONTHS}
      onChange={(range) => {
        if (range.from) setPeriodFrom(dayjs(range.from).format('YYYY-MM-DD'));
        if (range.to) setPeriodTo(dayjs(range.to).format('YYYY-MM-DD'));
      }}
    />
  );

  const modeToggle = (
    <ToggleButtonGroup
      testId="usage-mode-toggle"
      size="md"
      value={mode}
      onChange={(_e, val) => val && setMode(val as UsageMode)}
    >
      <ToggleButton
        value="organizations"
        text="Organizations"
        testId="usage-mode-organizations"
      />
      <ToggleButton value="total" text="Total" testId="usage-mode-total" />
    </ToggleButtonGroup>
  );

  return (
    <Stack gap={24}>
      {/* Overview card – always current month */}
      {isOverviewLoading ? (
        <Skeleton
          variant="rectangular"
          height={122}
          sx={{ borderRadius: '4px' }}
          data-testid="detail-overview-loading"
        />
      ) : (
        overviewDetail && <DetailOverviewCard detail={overviewDetail} />
      )}

      {/* Usage over time chart with date range picker */}
      {(detail || isLoading) && (
        <UsageOverTimeChart
          detail={detail}
          isLoading={isLoading}
          selectedEntityIds={selectedRows}
          actions={rangePicker}
          headerControls={modeToggle}
          periodFrom={periodFrom}
          periodTo={periodTo}
          onEntityColorMapChange={setEntityColorMap}
          mode={mode}
        />
      )}

      {/* Table with tabs */}
      <Stack gap={8}>
        <Tabs
          value={activeTab}
          onChange={(_, tab) =>
            setActiveTab(tab as 'perOrganization' | 'daily')
          }
          size="small"
        >
          <Tab value="perOrganization" label="Per organization" />
          <Tab
            value="daily"
            label={
              <Stack direction="row" gap={4} alignItems="center">
                Daily
                <Tooltip
                  title="Calculated once at the end of the day"
                  placement="top"
                  arrow
                  disableInteractive
                >
                  <Box display="flex">
                    <Icons.Info size={14} />
                  </Box>
                </Tooltip>
              </Stack>
            }
          />
        </Tabs>

        {activeTab === 'perOrganization' && (
          <EnterpriseEntitiesTable
            entities={entities}
            entityColorMap={entityColorMap}
            unit={unit}
            isLoading={isLoading}
            isError={isError}
            refetch={refetch}
            selectedRows={selectedRows}
            onRowsSelected={handleRowsSelected}
            maxSelection={MAX_SELECTED_ORGANIZATIONS}
            enterpriseId={enterpriseId}
            feature={feature}
          />
        )}
        {activeTab === 'daily' && (
          <DailyUsageTable
            dailyUsages={detail?.dailyUsages ?? []}
            unit={unit}
            isLoading={isLoading}
            isError={isError}
            refetch={refetch}
          />
        )}
      </Stack>

      <PlatformUsageFooter
        selectedRows={selectedRows}
        entities={entities}
        unit={unit}
        activeTab={activeTab}
      />
    </Stack>
  );
};
